import React, { useState, useEffect } from 'react';
import { FaShieldAlt, FaTerminal, FaMousePointer, FaEye, FaCalendarAlt, FaPowerOff, FaSave } from 'react-icons/fa';
import API_BASE_URL from '../../config/api';

const SecuritySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    disable_dev_tools: '0',
    disable_right_click: '0',
    enable_watermark: '0',
    grace_period_days: '16',
    master_security_mode: '1'
  });

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/manage_settings.php`);
      const result = await response.json();
      if (result.status === 'success') {
        setSettings(result.data);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const [pin, setPin] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pinError, setPinError] = useState(false);
  const CORRECT_PIN = "778899";

  useEffect(() => {
    if (isAuthenticated) {
      fetchSettings();
    }
  }, [isAuthenticated]);

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pin === CORRECT_PIN) {
      setIsAuthenticated(true);
    } else {
      setPinError(true);
      setPin('');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/manage_settings.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      const result = await response.json();
      if (result.status === 'success') {
        alert("Security configurations applied globally.");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center py-20 h-full">
        <div className="bg-white p-8 rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-slate-50 text-slate-400 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl shadow-inner border border-slate-100">
            <FaShieldAlt />
          </div>
          <h2 className="text-xl font-black text-slate-800 mb-2">Security Verification</h2>
          <p className="text-sm font-medium text-slate-500 mb-6 leading-relaxed">Please enter the master PIN to manage security settings.</p>
          
          <form onSubmit={handlePinSubmit} className="space-y-4">
            <div>
              <input 
                type="password" 
                value={pin}
                onChange={(e) => { setPin(e.target.value); setPinError(false); }}
                placeholder="Enter PIN"
                className={`w-full px-4 py-3 bg-slate-50 border ${pinError ? 'border-red-300 focus:border-red-500 text-red-600' : 'border-slate-100 focus:border-orange-500 text-slate-800'} rounded-xl outline-none font-black text-center text-xl tracking-[0.5em] transition-colors`}
                autoFocus
              />
              {pinError && <p className="text-xs font-bold text-red-500 mt-2 uppercase tracking-widest">Incorrect PIN</p>}
            </div>
            <button type="submit" className="w-full py-3.5 bg-slate-900 text-white font-black rounded-xl hover:bg-black transition-colors uppercase tracking-widest text-sm shadow-md">
              Verify Access
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3 text-orange-500">
          <FaShieldAlt size={24} />
          <h2 className="text-2xl font-black text-slate-800">Security Settings</h2>
        </div>
        <p className="text-slate-500 font-medium text-sm">Manage platform-wide security restrictions and protections.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        {/* Dev Tools Restriction */}
        <SecurityCard 
          icon={<FaTerminal />}
          title="Developer Options Restriction"
          description="Disable F12, Inspect Element, and Ctrl+Shift+I globally."
          active={settings.disable_dev_tools === '1'}
          onToggle={() => setSettings({...settings, disable_dev_tools: settings.disable_dev_tools === '1' ? '0' : '1'})}
        />

        {/* Right Click Restriction */}
        <SecurityCard 
          icon={<FaMousePointer />}
          title="Right-Click Restriction"
          description="Prevent users from opening the browser context menu."
          active={settings.disable_right_click === '1'}
          onToggle={() => setSettings({...settings, disable_right_click: settings.disable_right_click === '1' ? '0' : '1'})}
        />

        {/* Anti-Recording Watermark */}
        <SecurityCard 
          icon={<FaEye />}
          title="Anti-Recording Watermark"
          description="Show semi-transparent student identity over video content."
          active={settings.enable_watermark === '1'}
          onToggle={() => setSettings({...settings, enable_watermark: settings.enable_watermark === '1' ? '0' : '1'})}
        />

        {/* Master Mode */}
        <SecurityCard 
          icon={<FaPowerOff />}
          title="Developer Master Mode"
          description="Global switch for all security measures."
          active={settings.master_security_mode === '1'}
          onToggle={() => setSettings({...settings, master_security_mode: settings.master_security_mode === '1' ? '0' : '1'})}
          isMaster
        />

        {/* Grace Period */}
        <div className="md:col-span-2 bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 bg-orange-50 text-orange-500 rounded-2xl flex items-center justify-center text-xl shadow-inner border border-orange-100">
              <FaCalendarAlt />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-slate-800 text-lg">Month Grace Period (Day)</h3>
                {(parseInt(new Date().getDate()) <= parseInt(settings.grace_period_days)) ? (
                  <span className="px-2 py-0.5 bg-green-50 text-green-600 rounded-full text-[8px] font-black uppercase tracking-widest flex items-center gap-1">
                    <span className="w-1 h-1 bg-green-500 rounded-full animate-pulse"></span> Active Now
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-slate-50 text-slate-400 rounded-full text-[8px] font-black uppercase tracking-widest">
                    Expired
                  </span>
                )}
              </div>
              <p className="text-slate-400 text-sm font-medium">Automatic unlock until this day of the month. (Today: {new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'long' })})</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input 
              type="number" 
              value={settings.grace_period_days}
              onChange={(e) => setSettings({...settings, grace_period_days: e.target.value})}
              className="w-24 px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl outline-none focus:border-orange-500 font-black text-center text-lg text-slate-800"
            />
            <span className="font-black text-slate-400 text-xs uppercase tracking-widest">Day</span>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-4">
        <button 
          onClick={handleSave}
          disabled={saving}
          className="px-10 py-4 bg-orange-500 hover:bg-orange-600 text-white font-black rounded-2xl shadow-xl shadow-orange-100 transition-all flex items-center gap-3 active:scale-95 disabled:opacity-50"
        >
          {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <FaSave />}
          Save Security Policy
        </button>
      </div>
    </div>
  );
};

const SecurityCard = ({ icon, title, description, active, onToggle, isMaster }) => (
  <div className={`bg-white rounded-[2rem] p-8 border ${active ? 'border-orange-200 bg-orange-50/10' : 'border-slate-100'} shadow-sm transition-all flex flex-col justify-between gap-6 group`}>
    <div className="space-y-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg ${active ? 'bg-orange-500 text-white' : 'bg-slate-50 text-slate-400 group-hover:bg-slate-100'} transition-colors`}>
        {icon}
      </div>
      <div>
        <h3 className="font-bold text-slate-800 text-lg">{title}</h3>
        <p className="text-slate-400 text-sm font-medium leading-relaxed">{description}</p>
      </div>
    </div>
    <div className="flex items-center justify-between pt-4 border-t border-slate-50">
      <span className={`text-[10px] font-black uppercase tracking-widest ${active ? 'text-orange-600' : 'text-slate-300'}`}>
        {active ? 'Status: Protected' : 'Status: Disabled'}
      </span>
      <button 
        onClick={onToggle}
        className={`w-14 h-7 rounded-full relative transition-all duration-300 ${active ? 'bg-orange-500' : 'bg-slate-200'}`}
      >
        <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-all duration-300 ${active ? 'left-8' : 'left-1'}`}></div>
      </button>
    </div>
  </div>
);

export default SecuritySettings;
